import { removeUndefinedValues } from "../utils/firestore";
import {
  CollectionPaths,
  ConflictError,
  NotFoundError,
} from "@order-up/common";
import { ISalesCategory, SalesCategory } from "@order-up/models";
import { SalesCategorySchema } from "@order-up/schemas";
import {
  DocumentWithId,
  FirestoreRepository,
} from "../interfaces/FirestoreRepository";
import { createLogger } from "../utils/logger";
import { counterService } from "./counters";
import { resolveLocationDocId, resolveTenantDocId } from "./resolveDocIds";

const logger = createLogger("SalesCategoryService");

/**
 * Gets all sales categories for a restaurant
 */
const getSalesCategoriesByRestaurantId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<SalesCategory[]> => {
  logger.debug(
    `Getting sales categories for tenantId: ${tenantId}, restaurantId: ${locationNumericId}`,
  );

  try {
    const tenantDocId = await resolveTenantDocId(tenantId);
    if (!tenantDocId) {
      logger.warn(`Tenant with ID ${tenantId} not found`);
      return [];
    }

    const locationDocId = await resolveLocationDocId(
      tenantDocId,
      locationNumericId,
    );
    if (!locationDocId) {
      logger.warn(
        `Restaurant with ID ${locationNumericId} not found in tenant ${tenantId}`,
      );
      return [];
    }

    const collectionPath = CollectionPaths.salesCategories(
      tenantDocId,
      locationDocId,
    );
    const salesCategoryRepo = new FirestoreRepository<SalesCategory>(
      collectionPath,
      SalesCategory,
    );
    const salesCategories = await salesCategoryRepo.find();

    logger.debug(
      `Retrieved ${salesCategories.length} sales categories for restaurant ${locationNumericId}`,
    );
    return salesCategories;
  } catch (error) {
    logger.error(
      `Error fetching sales categories for restaurant ${locationNumericId}`,
      { error: error instanceof Error ? error.message : String(error) },
    );
    throw error;
  }
};

/**
 * Gets a sales category by its business ID
 */
const getSalesCategoryById = async (
  tenantId: number,
  locationNumericId: number,
  salesCategoryId: number,
): Promise<SalesCategory | null> => {
  logger.debug(
    `Getting sales category ${salesCategoryId} for restaurant ${locationNumericId}`,
  );

  try {
    const tenantDocId = await resolveTenantDocId(tenantId);
    if (!tenantDocId) return null;

    const locationDocId = await resolveLocationDocId(
      tenantDocId,
      locationNumericId,
    );
    if (!locationDocId) return null;

    const collectionPath = CollectionPaths.salesCategories(
      tenantDocId,
      locationDocId,
    );
    const salesCategoryRepo = new FirestoreRepository<SalesCategory>(
      collectionPath,
      SalesCategory,
    );
    const results = await salesCategoryRepo.find([
      { field: "salesCategoryId", operator: "==", value: salesCategoryId },
    ]);

    return results.length > 0 ? results[0] : null;
  } catch (error) {
    logger.error(`Error fetching sales category ${salesCategoryId}`, {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Creates a new sales category
 */
const createSalesCategory = async (
  tenantId: number,
  locationNumericId: number,
  salesCategoryData: ISalesCategory,
): Promise<DocumentWithId<SalesCategory>> => {
  logger.debug(`Creating sales category for restaurant ${locationNumericId}`, {
    salesCategoryData: salesCategoryData as unknown as Record<string, unknown>,
  });

  try {
    const tenantDocId = await resolveTenantDocId(tenantId);
    if (!tenantDocId) {
      throw new Error(`Tenant with ID ${tenantId} not found`);
    }

    const locationDocId = await resolveLocationDocId(
      tenantDocId,
      locationNumericId,
    );
    if (!locationDocId) {
      throw new Error(`Restaurant with ID ${locationNumericId} not found`);
    }

    // Use provided IDs if they exist, otherwise generate new ones
    let salesCategoryId: number;
    let guid: string;

    if (salesCategoryData.salesCategoryId && salesCategoryData.guid) {
      // Use provided IDs from mock data to maintain relationships
      salesCategoryId = salesCategoryData.salesCategoryId;
      guid = salesCategoryData.guid;
      logger.debug(
        `Using provided IDs for: ${salesCategoryData.name} (ID: ${salesCategoryId})`,
      );
    } else {
      // Generate new IDs when not provided
      const generated = await counterService.generateSalesCategoryId(
        tenantId,
        locationNumericId,
      );
      salesCategoryId = generated.salesCategoryId;
      guid = generated.guid;
      logger.debug(
        `Generated new IDs for: ${salesCategoryData.name} (ID: ${salesCategoryId})`,
      );
    }

    // Prepare sales category data with generated IDs for validation
    const salesCategoryForValidation = {
      ...salesCategoryData,
      salesCategoryId,
      guid,
    };

    // Validate the data using the schema (without server timestamps)
    SalesCategorySchema.parse(salesCategoryForValidation);

    // Check for uniqueness before creating
    const conflict = await checkNameExists(
      tenantId,
      locationNumericId,
      salesCategoryData.name,
    );
    if (conflict.taken) {
      const message = conflict.isArchived
        ? `A sales category with the name "${salesCategoryData.name}" already exists but is archived`
        : `A sales category with the name "${salesCategoryData.name}" already exists`;
      throw new ConflictError(message);
    }

    const codeExists = await checkCodeExists(
      tenantId,
      locationNumericId,
      salesCategoryData.code,
    );
    if (codeExists) {
      throw new ConflictError(
        `A sales category with the code "${salesCategoryData.code}" already exists`,
      );
    }

    logger.debug(
      `Sales category data passed validation for: ${salesCategoryData.name}`,
    );

    // Create sales category payload with server timestamps after validation
    const salesCategoryPayload: any = removeUndefinedValues({
      ...salesCategoryForValidation,
      createdAt: FirestoreRepository.getServerTimestamp(),
      updatedAt: FirestoreRepository.getServerTimestamp(),
    });

    const collectionPath = CollectionPaths.salesCategories(
      tenantDocId,
      locationDocId,
    );
    const salesCategoryRepo = new FirestoreRepository<SalesCategory>(
      collectionPath,
      SalesCategory,
    );
    const createdDoc = await salesCategoryRepo.create(salesCategoryPayload);

    logger.debug(`Successfully created sales category`, {
      createdDoc: createdDoc as unknown as Record<string, unknown>,
    });
    return createdDoc;
  } catch (error) {
    logger.error(`Error creating sales category`, {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Updates an existing sales category
 */
const updateSalesCategory = async (
  tenantId: number,
  locationNumericId: number,
  salesCategoryId: number,
  salesCategoryData: Partial<ISalesCategory>,
): Promise<DocumentWithId<SalesCategory>> => {
  logger.debug(
    `Updating sales category ${salesCategoryId} for restaurant ${locationNumericId}:`,
    salesCategoryData,
  );

  try {
    const existingSalesCategory = await getSalesCategoryById(
      tenantId,
      locationNumericId,
      salesCategoryId,
    );
    if (!existingSalesCategory) {
      throw new NotFoundError(
        `Sales category with ID ${salesCategoryId} not found`,
      );
    }

    const tenantDocId = await resolveTenantDocId(tenantId);
    const locationDocId = await resolveLocationDocId(
      tenantDocId!,
      locationNumericId,
    );

    const collectionPath = CollectionPaths.salesCategories(
      tenantDocId!,
      locationDocId!,
    );
    const salesCategoryRepo = new FirestoreRepository<SalesCategory>(
      collectionPath,
      SalesCategory,
    );

    // Check for uniqueness if name or code is being updated
    if (salesCategoryData.name) {
      const conflict = await checkNameExists(
        tenantId,
        locationNumericId,
        salesCategoryData.name,
        salesCategoryId,
      );
      if (conflict.taken) {
        const message = conflict.isArchived
          ? `A sales category with the name "${salesCategoryData.name}" already exists but is archived`
          : `A sales category with the name "${salesCategoryData.name}" already exists`;
        throw new ConflictError(message);
      }
    }

    if (salesCategoryData.code) {
      const codeExists = await checkCodeExists(
        tenantId,
        locationNumericId,
        salesCategoryData.code,
        salesCategoryId,
      );
      if (codeExists) {
        throw new ConflictError(
          `A sales category with the code "${salesCategoryData.code}" already exists`,
        );
      }
    }

    // Prepare update data with server timestamp and remove undefined values
    const updatePayload = removeUndefinedValues({
      ...salesCategoryData,
      updatedAt: FirestoreRepository.getServerTimestamp(),
    });

    // Ensure we don't overwrite these core fields during update
    delete (updatePayload as any).salesCategoryId;
    delete (updatePayload as any).guid;
    delete (updatePayload as any).createdAt;

    // Update the document
    await salesCategoryRepo.updateById(
      (existingSalesCategory as any).docId,
      updatePayload,
    );

    // Refetch the updated document to get accurate server timestamps
    const updatedSalesCategory = await salesCategoryRepo.getById(
      (existingSalesCategory as any).docId,
    );
    if (!updatedSalesCategory) {
      throw new NotFoundError(
        `Failed to fetch updated sales category with ID ${salesCategoryId} after update`,
      );
    }

    logger.debug(`Successfully updated sales category`, {
      updatedSalesCategory: updatedSalesCategory as unknown as Record<
        string,
        unknown
      >,
    });
    return updatedSalesCategory;
  } catch (error) {
    logger.error(`Error updating sales category ${salesCategoryId}`, {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Archives a sales category (soft delete)
 */
const archiveSalesCategory = async (
  tenantId: number,
  locationNumericId: number,
  salesCategoryId: number,
): Promise<DocumentWithId<SalesCategory>> => {
  logger.debug(
    `Archiving sales category ${salesCategoryId} for restaurant ${locationNumericId}`,
  );

  try {
    const existingSalesCategory = await getSalesCategoryById(
      tenantId,
      locationNumericId,
      salesCategoryId,
    );
    if (!existingSalesCategory) {
      throw new NotFoundError(
        `Sales category with ID ${salesCategoryId} not found`,
      );
    }

    const tenantDocId = await resolveTenantDocId(tenantId);
    const locationDocId = await resolveLocationDocId(
      tenantDocId!,
      locationNumericId,
    );

    const collectionPath = CollectionPaths.salesCategories(
      tenantDocId!,
      locationDocId!,
    );
    const salesCategoryRepo = new FirestoreRepository<SalesCategory>(
      collectionPath,
      SalesCategory,
    );

    // Soft delete: set isArchived to true and archivedAt timestamp
    const archiveData = {
      isArchived: true,
      archivedAt: FirestoreRepository.getServerTimestamp(),
      updatedAt: FirestoreRepository.getServerTimestamp(),
    };

    await salesCategoryRepo.updateById(
      (existingSalesCategory as any).docId,
      archiveData,
    );

    // Refetch the updated document to get accurate server timestamps
    const updatedSalesCategory = await salesCategoryRepo.getById(
      (existingSalesCategory as any).docId,
    );
    if (!updatedSalesCategory) {
      throw new NotFoundError(
        `Failed to fetch archived sales category with ID ${salesCategoryId} after update`,
      );
    }

    logger.debug(`Successfully archived sales category ${salesCategoryId}`);
    return updatedSalesCategory;
  } catch (error) {
    logger.error(`Error archiving sales category ${salesCategoryId}`, {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Restores an archived sales category
 */
const restoreSalesCategory = async (
  tenantId: number,
  locationNumericId: number,
  salesCategoryId: number,
): Promise<DocumentWithId<SalesCategory>> => {
  logger.debug(
    `Restoring sales category ${salesCategoryId} for restaurant ${locationNumericId}`,
  );

  try {
    const existingSalesCategory = await getSalesCategoryById(
      tenantId,
      locationNumericId,
      salesCategoryId,
    );
    if (!existingSalesCategory) {
      throw new NotFoundError(
        `Sales category with ID ${salesCategoryId} not found`,
      );
    }

    // Check for duplicate name before restoring
    const nameExists = await checkNameExists(
      tenantId,
      locationNumericId,
      existingSalesCategory.name,
      salesCategoryId,
    );
    if (nameExists.taken) {
      throw new ConflictError(
        `Cannot restore: A category with the name "${existingSalesCategory.name}" already exists`,
      );
    }

    const codeExists = await checkCodeExists(
      tenantId,
      locationNumericId,
      existingSalesCategory.code,
      salesCategoryId,
    );
    if (codeExists) {
      throw new ConflictError(
        `Cannot restore: A category with the code "${existingSalesCategory.code}" already exists`,
      );
    }

    const tenantDocId = await resolveTenantDocId(tenantId);
    const locationDocId = await resolveLocationDocId(
      tenantDocId!,
      locationNumericId,
    );

    const collectionPath = CollectionPaths.salesCategories(
      tenantDocId!,
      locationDocId!,
    );
    const salesCategoryRepo = new FirestoreRepository<SalesCategory>(
      collectionPath,
      SalesCategory,
    );

    // Restore: set isArchived to false and clear archivedAt
    const restoreData = {
      isArchived: false,
      archivedAt: FirestoreRepository.deleteField(),
      updatedAt: FirestoreRepository.getServerTimestamp(),
    };

    await salesCategoryRepo.updateById(
      (existingSalesCategory as any).docId,
      restoreData,
    );

    // Refetch the updated document to get accurate server timestamps
    const updatedSalesCategory = await salesCategoryRepo.getById(
      (existingSalesCategory as any).docId,
    );
    if (!updatedSalesCategory) {
      throw new NotFoundError(
        `Failed to fetch restored sales category with ID ${salesCategoryId} after update`,
      );
    }

    logger.debug(`Successfully restored sales category ${salesCategoryId}`);
    return updatedSalesCategory;
  } catch (error) {
    logger.error(`Error restoring sales category ${salesCategoryId}`, {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Checks if a sales category name already exists (case-insensitive)
 * Excludes archived categories from uniqueness check
 * Returns an object with 'taken' and 'isArchived' flags.
 * @param excludeId - Optional ID to exclude from the check (for edit operations)
 */
const checkNameExists = async (
  tenantId: number,
  locationNumericId: number,
  name: string,
  excludeId?: number,
): Promise<{ taken: boolean; isArchived?: boolean }> => {
  logger.debug(
    `Checking if sales category name "${name}" exists for restaurant ${locationNumericId}`,
  );

  try {
    const salesCategories = await getSalesCategoriesByRestaurantId(
      tenantId,
      locationNumericId,
    );

    const normalizedName = name.trim().toLowerCase();
    const conflict = salesCategories.find((c) => {
      if (c.name.toLowerCase() !== normalizedName) return false;
      if (excludeId !== undefined) {
        return String(c.salesCategoryId) !== String(excludeId);
      }
      return true;
    });

    const result = {
      taken: !!conflict,
      isArchived: conflict ? conflict.isArchived === true : undefined,
    };

    logger.debug(`Name "${name}" conflict:`, result);
    return result;
  } catch (error) {
    logger.error(`Error checking if name exists`, {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Checks if a sales category code already exists (case-insensitive)
 * Excludes archived categories from uniqueness check
 * @param excludeSalesCategoryId - Optional ID to exclude from the check (for edit operations)
 */
const checkCodeExists = async (
  tenantId: number,
  locationNumericId: number,
  code: string,
  excludeSalesCategoryId?: number,
): Promise<boolean> => {
  logger.debug(
    `Checking if sales category code "${code}" exists for restaurant ${locationNumericId}`,
  );

  try {
    const salesCategories = await getSalesCategoriesByRestaurantId(
      tenantId,
      locationNumericId,
    );

    // Check for case-insensitive match, excluding archived categories and the category being edited
    const normalizedCode = code.trim().toLowerCase();
    const exists = salesCategories.some(
      (category) =>
        category.code.toLowerCase() === normalizedCode &&
        !category.isArchived &&
        category.salesCategoryId !== excludeSalesCategoryId,
    );

    logger.debug(`Code "${code}" exists: ${exists}`);
    return exists;
  } catch (error) {
    logger.error(`Error checking if code exists`, {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Creates multiple sales categories in a batch operation using Firestore writeBatch()
 */
const createSalesCategoriesBatch = async (
  tenantId: number,
  locationNumericId: number,
  salesCategoriesData: ISalesCategory[],
): Promise<DocumentWithId<SalesCategory>[]> => {
  logger.debug(
    `Creating ${salesCategoriesData.length} sales categories in batch for restaurant ${locationNumericId}`,
  );

  try {
    const tenantDocId = await resolveTenantDocId(tenantId);
    if (!tenantDocId) {
      throw new Error(`Tenant with ID ${tenantId} not found`);
    }

    const locationDocId = await resolveLocationDocId(
      tenantDocId,
      locationNumericId,
    );
    if (!locationDocId) {
      throw new Error(`Restaurant with ID ${locationNumericId} not found`);
    }

    const collectionPath = CollectionPaths.salesCategories(
      tenantDocId,
      locationDocId,
    );
    const salesCategoryRepo = new FirestoreRepository<SalesCategory>(
      collectionPath,
      SalesCategory,
    );

    // Step 1: Generate all IDs and validate all data first
    const validatedPayloads: any[] = [];

    for (const salesCategoryData of salesCategoriesData) {
      // Use provided IDs if they exist, otherwise generate new ones
      let salesCategoryId: number;
      let guid: string;

      if (salesCategoryData.salesCategoryId && salesCategoryData.guid) {
        // Use provided IDs from mock data to maintain relationships
        salesCategoryId = salesCategoryData.salesCategoryId;
        guid = salesCategoryData.guid;
        logger.debug(
          `Using provided IDs for: ${salesCategoryData.name} (ID: ${salesCategoryId})`,
        );
      } else {
        // Generate new IDs when not provided
        const generated = await counterService.generateSalesCategoryId(
          tenantId,
          locationNumericId,
        );
        salesCategoryId = generated.salesCategoryId;
        guid = generated.guid;
        logger.debug(
          `Generated new IDs for: ${salesCategoryData.name} (ID: ${salesCategoryId})`,
        );
      }

      // Prepare sales category data with generated IDs for validation
      const salesCategoryForValidation = {
        ...salesCategoryData,
        salesCategoryId,
        guid,
      };

      // Validate the data using the schema (without server timestamps)
      SalesCategorySchema.parse(salesCategoryForValidation);

      // Create sales category payload (server timestamps will be added by batchCreate)
      const salesCategoryPayload: any = removeUndefinedValues(
        salesCategoryForValidation,
      );
      validatedPayloads.push(salesCategoryPayload);
    }

    logger.debug(
      `All ${validatedPayloads.length} sales categories validated, executing batch create`,
    );

    // Step 2: Batch create all documents using Firestore writeBatch()
    const salesCategories =
      await salesCategoryRepo.batchCreate(validatedPayloads);

    logger.debug(
      `Successfully created ${salesCategories.length} sales categories in batch`,
    );
    return salesCategories;
  } catch (error) {
    logger.error(`Error creating sales categories in batch`, {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

export const salesCategoryService = {
  getSalesCategoriesByRestaurantId,
  getSalesCategoryById,
  createSalesCategory,
  updateSalesCategory,
  archiveSalesCategory,
  restoreSalesCategory,
  checkNameExists,
  checkCodeExists,
  createSalesCategoriesBatch,
};
